import { useState } from "react";
import { Link2, FileText, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { useQuota } from "@/lib/quota-context";
import { analyzeListing } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

export const InputPanel = ({ onResult, onOpenPaywall }) => {
  const { t, lang } = useLang();
  const { setQuotaFromAnalyze, refresh: refreshQuota } = useQuota();
  const [mode, setMode] = useState("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (mode === "url" && !url.trim()) {
      toast.error(t("input.errEmpty"));
      return;
    }
    if (mode === "text" && !text.trim()) {
      toast.error(t("input.errEmpty"));
      return;
    }
    setLoading(true);
    try {
      const payload =
        mode === "url"
          ? { url: url.trim(), language: lang }
          : { text: text.trim(), language: lang };
      const result = await analyzeListing(payload);
      if (result?.quota) setQuotaFromAnalyze(result.quota);
      trackEvent("analysis_completed", {
        input_mode: mode,
        town: result?.town || null,
        score: result?.total_score ?? null,
        status: result?.status || null,
        language: lang,
      });
      onResult?.(result);
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 402) {
        refreshQuota();
        const isGuest = detail?.tier === "guest";
        trackEvent("paywall_shown", {
          reason: "quota_exhausted",
          tier: isGuest ? "guest" : "user",
        });
        toast.error(isGuest ? t("quota.guestExhausted") : t("quota.userExhausted"));
        // Always surface the modal — for guests it shows a prominent
        // "Register Free → 3 extra reports" CTA, for users it shows the
        // €1.99 credit / €9.90 Premium cards. A toast alone is too easy
        // to miss on mobile.
        onOpenPaywall?.();
      } else if (code === 429) {
        toast.error(
          lang === "it"
            ? "Server occupato con un'altra valutazione. Riprova fra qualche secondo."
            : "Server busy with another review. Try again in a few seconds."
        );
      } else if (code === 502 || code === 504) {
        // Scrape fallito — il sito blocca i bot (DataDome/Cloudflare) o ha
        // timeout. La modalità "Testo libero" funziona sempre perché
        // bypassa lo scraper.
        toast.error(
          lang === "it"
            ? "Non riusciamo a leggere questo annuncio (il sito blocca gli accessi automatici). Copia il testo dell'annuncio e usa la tab 'Testo libero' qui sopra."
            : "We can't read this listing (the site blocks automated access). Copy the listing text and use the 'Free text' tab above.",
          { duration: 8000 }
        );
        setMode("text");
      } else if (code === 524 || code === 408 || err?.code === "ECONNABORTED") {
        // Cloudflare 524 / browser timeout / axios abort — analisi troppo lenta.
        toast.error(
          lang === "it"
            ? "L'analisi sta impiegando troppo tempo (timeout). Riprova fra qualche secondo: il server potrebbe essere sotto carico."
            : "The analysis is taking too long (timeout). Please retry in a few seconds: the server may be under load.",
          { duration: 8000 }
        );
      } else if (!err?.response) {
        // Network error / CORS / browser block: no HTTP response at all.
        // Common on flaky mobile data or content-blockers.
        toast.error(
          lang === "it"
            ? "Connessione interrotta. Verifica la tua connessione internet e riprova."
            : "Connection lost. Please check your internet connection and try again.",
          { duration: 8000 }
        );
      } else {
        // Catch-all: log dettagli per debug + messaggio utente con codice HTTP
        // così possiamo diagnosticare in produzione anche senza dev tools.
        const rawDetail = err?.response?.data?.detail;
        const detailStr =
          typeof rawDetail === "string"
            ? rawDetail
            : rawDetail?.code || rawDetail?.message || null;
        // eslint-disable-next-line no-console
        console.error("[analyze] unexpected error", {
          status: code,
          detail: rawDetail,
          message: err?.message,
        });
        const base = t("input.errFail");
        toast.error(
          detailStr
            ? `${base} (${code || "?"}: ${detailStr})`
            : `${base} (${code || "network"})`,
          { duration: 8000 }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="input-panel"
      className="bg-white border-2 border-[#282C2A] shadow-paper rounded-sm relative"
    >
      {/* Olive accent strip — pops the panel out of the page chrome. */}
      <div aria-hidden className="absolute -top-2 left-0 right-0 h-2 bg-[#4A5D23]" />
      <div className="flex border-b border-[#E6E4DD]">
        <button
          type="button"
          data-testid="tab-url"
          onClick={() => setMode("url")}
          className={`flex-1 px-6 py-4 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2 transition-colors ${
            mode === "url"
              ? "bg-[#282C2A] text-white"
              : "text-[#6C706D] hover:text-[#282C2A]"
          }`}
        >
          <Link2 size={15} strokeWidth={1.5} />
          {t("input.tabUrl")}
        </button>
        <button
          type="button"
          data-testid="tab-text"
          onClick={() => setMode("text")}
          className={`flex-1 px-6 py-4 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2 transition-colors border-l border-[#E6E4DD] ${
            mode === "text"
              ? "bg-[#282C2A] text-white"
              : "text-[#6C706D] hover:text-[#282C2A]"
          }`}
        >
          <FileText size={15} strokeWidth={1.5} />
          {t("input.tabText")}
        </button>
      </div>

      <form onSubmit={submit} className="p-6 md:p-8">
        {mode === "url" ? (
          <input
            data-testid="input-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t("input.placeholderUrl")}
            className="w-full px-0 py-3 text-lg border-0 border-b border-[#E6E4DD] focus:outline-none focus:border-[#4A5D23] bg-transparent font-mono placeholder:text-[#A8AAA7]"
            autoFocus
          />
        ) : (
          <textarea
            data-testid="input-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("input.placeholderText")}
            rows={7}
            className="w-full px-4 py-3 text-base border border-[#E6E4DD] focus:outline-none focus:border-[#4A5D23] bg-[#FBFAF7] placeholder:text-[#A8AAA7] resize-y"
          />
        )}

        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <span className="text-xs text-[#6C706D]">{t("input.hint")}</span>
          <button
            type="submit"
            data-testid="analyze-btn"
            disabled={loading}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#4A5D23] hover:bg-[#3B4A1C] text-white font-semibold tracking-wider uppercase text-sm border border-[#3B4A1C] transition-colors disabled:opacity-60 shadow-[0_3px_0_#282C2A] hover:shadow-[0_2px_0_#282C2A] hover:translate-y-[1px]"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t("input.analyzing")}
              </>
            ) : (
              <>
                {t("input.analyze")}
                <ArrowRight size={16} strokeWidth={1.5} />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InputPanel;
